import React, { useEffect, useState } from "react";
import { AxiosResponse, AxiosError } from "axios";
import { Result, MedicalCode } from "../../interface/MedicalCodes";
import { MedicalCodesAxiosInstance } from "../../Network/MedicalCodesAxiosInstance";
import "react-toastify/dist/ReactToastify.css";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import DataTable2 from "shared/DataTable2";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
const MedicalCodes: React.FC = () => {
  const [items, setItems] = useState<MedicalCode>({ result: [] });
  const [icd, setIcd] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedIcd, setSelectedIcd] = useState<string | null>(null);

    // UI control states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const token = localStorage.getItem("token") || "";

     // Initialize toast
     const { toast } = useToast();

  useEffect(() => {
    console.log("Items:", items.result);
    fetchItems();
  }, []);

  const clear = () => {
    setIcd("");
    setIsEdit(false);
    setDescription("");
    setSelectedIcd(null);

  };

 // Dialog control handlers
    const handleAddClick = () => {
          clear();
        setIsDialogOpen(true); }

 const handleEditClick = (item: Result) => {
            handleEdit(item);
            setIsDialogOpen(true); }

  // Fetch ICD medical codes from the backend
  const fetchItems = async (): Promise<void> => {
    try {
      const response: AxiosResponse<{ success: boolean; result: Result[] }> =
        await MedicalCodesAxiosInstance.get("getIcd");
      setItems({ result: [...response.data.result].sort((a: any, b: any) => b.id - a.id) });

    } catch (error) {
      console.error("Error fetching items:", error);
      toast({
        title: "Error",
        description: "Failed to fetch ICD Code",
      })
    }
  };

  // Add or update ICD code
    const addItem = async (): Promise<void> => {
      try {
        const newGroup = {
          ICD: icd,
          description,
          token: token,
        };
  
      const response = await MedicalCodesAxiosInstance.post("addIcdcode", newGroup);
   
     if (response.data.success) {
          fetchItems();
          clear();
          setIsDialogOpen(false);
          toast({
                  title: "Success",
                  description:  " Icd code Added Successfully.",
                  });
        
        } else {
          toast({
                   title: "Error",
                  description: response.data.error  || "An unexpected error occurred.",
                    });
     
        }
      } catch (error: AxiosError | any) {
        console.error("Error saving Activity code:", error);
        toast({
          title: "Error",
        description:
           error.response?.data?.error || "An unexpected error occurred.",
       });
      }
    };


  // Set data for editing and show modal
  const handleEdit = (item: Result) => {
    setIcd(item.icd);
    setDescription(item.description);
    setIsEdit(true);
    setSelectedIcd(item.icd);
    setIsDialogOpen(true); // Show modal after setting data
  };

  const removeItem = async (icd: string | number): Promise<void> => {
    console.log("Deleting ICD code with icd:", icd); // Log to check the icd value
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response: AxiosResponse<{ success: boolean; message: string }> =
        await MedicalCodesAxiosInstance.delete(`delete/${icd}`, {
          headers,
          data: { token }, // Send the token in the body
        });

      if (response.data.success) {
        fetchItems(); // Refresh the list after deletion
        toast({
          title: "Success",
          description:  "ICD code Deleted Successfully.",
       });

      } else {
        toast({
          title: "Error",
        description:
         response.data.message  || "Failed to delete ICD code.",
       });
      }
    } catch (error: AxiosError | any) {
      console.error("Error removing ICD code:", error);
      toast({
        title: "Error",
      description:
      error.response?.data.error   || "Failed to delete ICD code.",
     });
    }
  };
  const updateItem = async (): Promise<void> => {
    try {
      if (!selectedIcd) {
        toast({
            title: "Error",
          description: "No ICD code selected for update",
         });
        return;
      }

      const updatedGroup = {
        oldICD: selectedIcd,
        newICD: icd,
        description,
        token,
      };

      const response = await MedicalCodesAxiosInstance.put(
        "updatemedical",
        updatedGroup
      );

      if (response.data.success) {
        fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({
            title: "Success",
            description:  "ICD code updated successfully.",
         });

} else {
toast({
         title: "Error",
       description:
        response.data.error  || "An unexpected error occurred.",
      });

  }} catch (error: AxiosError | any) {
console.error("Error saving ICD code:", error);
toast({
title: "Error",
description:
error.response?.data?.error || "An unexpected error occurred.",
});
}};
 

  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "icd",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          ICD
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Description
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button variant="outline"onClick={() => handleEdit(row.original)}>Edit</Button>
          <Button variant="destructive" onClick={() => removeItem(row.original.icd)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">


    <h5 className="mb-6 flex items-center gap-2" style={{fontWeight:"bolder" ,fontSize:"22px",  marginTop:"15px"}}>
    <span className="icon-container">
                  <i className="fa-solid fa-list"></i>
              </span>
              ICD Codes
        </h5>
      
   <DataTable2
          columns={columns}
          data={items.result}
          searchColumns={["icd"]}
          addButtonLabel="Add New ICD Code "
          showAddButton={true}
          onAdd={handleAddClick}
          onEditClick={handleEditClick}
        />
     <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedIcd === null
                ? "Add ICD Code "
                : "Edit ICD Code "}
            </DialogTitle>
            </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
      {/* First Column */}
      <div>

     <div className="mb-4">
              <Label htmlFor="ICD">ICD:</Label>
              <Input
  id="icd"
  value={icd}
  onChange={(e) => setIcd(e.target.value)}

  placeholder="ICD Code is required  "
/>

</div>                 

</div>
      {/* Second Column */}
      <div>
      <div className="mb-4">

<Label htmlFor="Description">Description</Label>
<Input
  id="Description"
  value={description}
  onChange={(e) => setDescription(e.target.value)}

  placeholder="Description is required  "
/>
</div>
</div>
 </div>
         
<DialogFooter className="gap-2 m-4">
            <Button
               onClick={isEdit ? updateItem : addItem}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {selectedIcd === null ? "Add Relation " : "Update Relation "}
            </Button>
            <Button
              variant="outline"
             
                onClick={(e) => { 
                  clear(); 
                  setIsDialogOpen(false);
                 }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    <Toaster />
    </div>
  );
};

export default MedicalCodes;
